import moment from 'moment'
import { Readable, Convertable, Compareable, Value } from './ModelUtil'

export default class PatientModel {
  constructor () {
    this.id = 0
    this.status = ''
    this.contact = {
      name: '',
      phone: '',
      line: '',
      email: '',
    }
    this.customerType = ''
    this.customerCompany = {
      preset: '',
      name: '',
      branch: '',
      address: '',
      email: '',
      mailAddress: '',
      taxId: '',
    }
    this.hasSameAddress = true
    this.photo = ''
    this.name = ''
    this.gender = null
    this.birthDate = ''
    this.weight = null
    this.height = null
    this.type = null
    this.hasDiseases = null
    this.diseases = []
    this.skills = []
    this.skillOptions = {
      woundCare: {
        level: 0,
      },
      injection: {
        detail: '',
      },
    }
    this.activity = {
      preparingFood: null,
    }
    this.environment = {
      pet: null,
      petValid: false,
    }
    this.note = ''
    this.doctorNote = ''
    this.patientNote = ''
    this.caregiverNote = ''
    this.createdAt = ''
    this.updatedAt = ''
    this.locations = []
    this.different = []
    this.canEditCustomerType = true
  }

  // Getter & Setter
  get age () {
    return Convertable.date.from(this.birthDate).toAge()
  }

  set age (newValue) {
    if (newValue && newValue >= 0) {
      this.birthDate = `${moment().year() - newValue}-01-01`
    } else {
      this.birthDate = ''
    }
  }

  // Class method
  static createWithDefaultSkill () {
    let patient = new PatientModel()
    patient.skills = [
      'vitalSign',
      'transferring',
      'basicPhysicalTherapy',
      'bedBath',
    ]
    return patient
  }

  static fromJSON (json) {
    let patient = new PatientModel()
    patient.id = json.id
    patient.lineId = json.lineId
    patient.status = json.status
    patient.contact = json.contact
    patient.contact.name = json.contact.name || json.name
    patient.customerType = json.customerType
    patient.customerCompany.preset = json.customerCompany.preset
    if (json.customerCompany.preset === '') patient.customerCompany.preset = 'MTL'
    patient.customerCompany.name = json.customerCompany.name
    patient.customerCompany.branch = json.customerCompany.branch
    patient.customerCompany.address = json.customerCompany.address
    patient.customerCompany.email = json.customerCompany.email
    patient.customerCompany.mailAddress = json.customerCompany.mailAddress
    patient.customerCompany.taxId = json.customerCompany.taxId
    patient.hasSameAddress = json.hasSameAddress
    patient.photo = json.photo
    patient.name = json.name
    patient.gender = json.gender
    patient.birthDate = json.birthDate
    patient.type = json.type
    patient.hasDiseases = json.hasDiseases
    patient.diseases = json.diseases
    patient.skills = json.skills
    patient.skillOptions = json.skillOptions
    patient.activity = json.activity
    patient.environment = json.environment
    patient.note = json.note
    patient.doctorNote = json.doctorNote
    patient.patientNote = json.patientNote
    patient.caregiverNote = json.caregiverNote
    patient.updatedAt = json.updatedAt
    patient.createdAt = json.createdAt
    patient.weight = Value.notZeroOrNull(json.weight)
    patient.height = Value.notZeroOrNull(json.height)
    patient.different = json.different
    patient.canEditCustomerType = json.canEditCustomerType
    return patient
  }

  transformToDisplayable () {
    this.gender = Readable.gender.from(this.gender)
    this.customerType = Readable.customer.type.from(this.customerType)
    this.customerCompany.preset = Readable.customerCompany.type.from(this.customerCompany.preset)
    this.type = Readable.patient.type.from(this.type)
    this.diseases = this.diseases.sort(Compareable.disease).map((item) => {
      return transformDiseaseToDisplayble(item)
    })
    this.skills = this.skills.sort(Compareable.skill)
    this.activity = activityToArray(this.activity)
    this.environment = environmentToArray(this.environment)
    this.createdAt = moment(this.createdAt).format('DD/MM/YYYY, HH:mm')
    this.updatedAt = moment(this.updatedAt).format('DD/MM/YYYY, HH:mm')
    return this
  }

  woundCareDisplay () {
    if (this.skills.includes('woundCareLevel1')) {
      this.skills.push('woundCare')
      this.skillOptions.woundCare.level = 1
      this.skills = this.skills.filter((item) => {
        return item !== 'woundCareLevel1'
      })
    }
    if (this.skills.includes('woundCareLevel2')) {
      this.skills.push('woundCare')
      this.skillOptions.woundCare.level = 2
      this.skills = this.skills.filter((item) => {
        return item !== 'woundCareLevel2'
      })
    }
    if (this.skills.includes('woundCareLevel3')) {
      this.skills.push('woundCare')
      this.skillOptions.woundCare.level = 3
      this.skills = this.skills.filter((item) => {
        return item !== 'woundCareLevel3'
      })
    }
    if (this.skills.includes('woundCareLevel4')) {
      this.skills.push('woundCare')
      this.skillOptions.woundCare.level = 4
      this.skills = this.skills.filter((item) => {
        return item !== 'woundCareLevel4'
      })
    }
    return this
  }
}

function environmentToArray (json) {
  let pet = []
  if (!json.pet) {
    pet.push('ไม่มีสัตว์เลี้ยง')
  } else {
    if (json.pet.dog) pet.push('มีสุนัข')
    if (json.pet.cat) pet.push('มีแมว')
  }
  return pet
}

function activityToArray (json) {
  let acts = []
  if (json.preparingFood) {
    acts.push('ทำอาหารให้ผู้ป่วย')
  }
  return acts
}

function transformDiseaseToDisplayble (item) {
  let name = `${Readable.disease.from(item.name)}`
  if (item.name === 'alzheimer' || item.name === 'psychiatric') {
    if (item.severe) {
      name += ` (ระดับ 2)`
    } else {
      name += ` (ระดับ 1)`
    }
  }
  let note = []
  if (item.note) note.push(item.note)
  switch (item.name) {
    case 'kidneyFailure':
      if (item.homeDialysis) note.push(Readable.subDisease.fromKidneyFailure('homeDialysis'))
      if (item.hospitalDialysis) note.push(Readable.subDisease.fromKidneyFailure('hospitalDialysis'))
      break
    case 'psychiatric':
      if (item.abusive) note.push(Readable.subDisease.fromPsychiatric('abusive'))
      if (item.sleepProblem) note.push(Readable.subDisease.fromPsychiatric('sleepProblem'))
      break
    case 'alzheimer':
      if (item.abusive) note.push(Readable.subDisease.fromAlzheimer('abusive'))
      if (item.sleepProblem) note.push(Readable.subDisease.fromAlzheimer('sleepProblem'))
      if (item.forgetful) note.push(Readable.subDisease.fromAlzheimer('forgetful'))
      break
  }
  let disease = {
    name: name,
    note: note,
  }
  return disease
}
